'use client'
import { useState, useEffect, useRef } from 'react'
import { useReactToPrint } from 'react-to-print'
import { Tooltip } from 'react-tooltip'
import {
  ExclamationCircleIcon,
  InformationCircleIcon,
  MapPinIcon
} from '@heroicons/react/20/solid'

import { publicGetRequest } from 'services/one'
import Timetable from 'components/Timetable'
import TableStaticPagination from 'components/TableStaticPagination'
import RadioFilter from 'components/RadioFilter'
import SelectFilter from 'components/SelectFilter'
import Spin from 'components/Spin'
import { useTorContext, useCaptainContext } from 'utils/context'
import { getMatchesCategories } from 'services/tors'
import { getRandomInt } from 'utils/maths'
import { capitalizeString, formatName } from 'utils/strings'
import { dateToString, isDeviceTimezoneSpanish } from 'utils/date'
import NoResults from 'components/NoResults'
import Button from 'components/Button'
import { datesAreOnSameDay } from 'utils/date'
import { GoBackAction } from 'components/GoBack'
import { torsCats } from 'config/const'
import Title from 'components/privat/Title'
import TeamsResultHeader from 'components/TeamsResultHeader'

import 'styles/draws.css'

const genderFilter = [
  {
    value: 'MASCULINO',
    title: 'Masculí'
  },
  {
    value: 'FEMENINO',
    title: 'Femení'
  }
]
const defaultGender = genderFilter[getRandomInt(2)].value
const AB = new Array(26).fill(1).map((_, i) => String.fromCharCode(65 + i))

const Games = ({ isTor }) => {
  const { tor, torDetails } = useTorContext() || {}
  const { teamFilterSelected } = useCaptainContext() || {}
  const [torColor, setTorColor] = useState(torDetails?.color)
  const [phases, setPhases] = useState([])
  const [phasesOpen, setPhasesOpen] = useState([])
  const [startingFromPhase, setStartingFromPhase] = useState()
  const [memoPhasesOpen, setMemoPhasesOpen] = useState([])
  const [genderFilterSelected, setGenderFilterSelected] =
    useState(defaultGender)
  const [categories, setCategories] = useState([])
  const [categoryFilterSelected, setCategoryFilterSelected] = useState('')
  const [categoryFilter, setCategoryFilter] = useState([])
  const [loading, setLoading] = useState(false)
  const [isGroups, setIsGroups] = useState()
  const [groupTable, setGroupTable] = useState(true)
  const componentToPrint = useRef()
  const [isPrinting, setIsPrinting] = useState(false)
  const promiseResolveRef = useRef(null)
  const [drawsMsgsList, setDrawsMsgsList] = useState({})
  const [drawsMsgs, setDrawsMsgs] = useState({})
  const [catsByGender, setCatsByGender] = useState()
  const [pdfLinks, setPdfLinks] = useState({})
  const [pdfLink, setPdfLink] = useState()
  const [matchDaysIdSelected, setMatchDaysIdSelected] = useState()
  const [matchData, setMatchData] = useState()
  const [teamsResult, setTeamsResult] = useState({})
  const showSpainTimezone = isDeviceTimezoneSpanish()

  const getMatchData = async () => {
    const matchDataReq = await publicGetRequest({
      id: matchDaysIdSelected,
      endpoint: 'TEAM_RESULTS'
    })
    setTeamsResult((teams) => ({ ...teams, ...matchDataReq?.Global }))
    setMatchData(
      matchDataReq.Matches?.map((match) => ({
        noDate: `Partits ${match.Number}`,
        result: `${match.Team1Set1}-${match.Team2Set1} / ${match.Team1Set2}-${
          match.Team2Set2
        } ${
          !(
            (match.Team1Set1 > match.Team2Set1 &&
              match.Team1Set2 > match.Team2Set2) ||
            (match.Team2Set1 > match.Team1Set1 &&
              match.Team2Set2 > match.Team1Set2)
          )
            ? `/ ${match.Team1Set3}-${match.Team2Set3}`
            : ''
        }`,
        played: !match.LocalWO && !match.VisitingWO,
        teams: [
          {
            name: capitalizeString(match.Team1PlayerNameA),
            imageUrl: match?.Team1PlayerImgA
          },
          {
            name: capitalizeString(match.Team1PlayerNameB),
            imageUrl: match?.Team1PlayerImgB
          },
          {
            name: capitalizeString(match.Team2PlayerNameA),
            imageUrl: match?.Team2PlayerImgA
          },
          {
            name: capitalizeString(match.Team2PlayerNameB),
            imageUrl: match?.Team2PlayerImgB
          }
        ],
        abandonment: match?.set1Abandonment
          ? 1
          : match?.set2Abandonment
          ? 2
          : match?.set3Abandonment
          ? 3
          : 0,
        winner: match?.Team1Wins ? 1 : match?.Team2Wins ? 2 : 0
      })) || []
    )
  }
  useEffect(() => {
    if (matchDaysIdSelected) {
      getMatchData()
    }
  }, [matchDaysIdSelected])

  const setGenderFilters = () => {
    if (categories.length) {
      const cats = []
      const messages = {}
      const _pdfLinks = {}
      if (
        genderFilter.length === 2 &&
        categories.find(({ Gender }) => Gender === 'MIXTO')
      ) {
        genderFilter.push({
          value: 'MIXTO',
          title: 'Mixt'
        })
        setGenderFilterSelected('MIXTO')
      }
      const _catsByGender = categories.filter(
        ({ Gender }) => Gender === genderFilterSelected
      )
      setCatsByGender(_catsByGender)
      _catsByGender.forEach((category) => {
        const fuse = [...category.Grups, ...category.Draws]
        return fuse.forEach((item) => {
          cats.push({
            title: capitalizeString(item.Name),
            value: item.Id
          })
          messages[item.Id] = {
            main: item?.MainComment,
            secondary: item?.SecondaryComment
          }
          if (item?.attachedDocument) {
            _pdfLinks[item.Id] = item?.attachedDocument
          }
        })
      })
      setCategoryFilter(cats)
      setCategoryFilterSelected(cats?.[0]?.value)
      setDrawsMsgsList(messages)
      setDrawsMsgs(messages[cats?.[0]?.value])
      setPdfLinks(_pdfLinks)
      setPdfLink(_pdfLinks[cats?.[0]?.value])
    }
  }

  const getCategories = async () => {
    const matchesCats = await getMatchesCategories(
      tor?.Info?.Id || teamFilterSelected.IdTeam
    )
    setCategories(matchesCats.Categories)
  }

  const getDrawsData = async () => {
    setDrawsMsgs({})
    if (Object.keys(pdfLinks).length && pdfLinks[categoryFilterSelected]) {
      setPdfLink(pdfLinks[categoryFilterSelected])
    } else {
      setLoading(true)
      setPdfLink()
      const torIdRequest =
        categoryFilterSelected || teamFilterSelected?.Draws?.[0]?.idDraw
      // Not sure if groups uses IdTournament but this way the bug
      // related to use IdTournament in captain private instead of IdDraw is fixed
      const torIdRequestGroups =
        torIdRequest || teamFilterSelected?.IdTournament

      if (torIdRequest || torIdRequestGroups) {
        setPhases([])
        const _isGroups = catsByGender?.find((category) =>
          category?.Grups.find(
            ({ Id }) => `${Id}` === `${categoryFilterSelected}`
          )
        )
        setIsGroups(_isGroups)
        try {
          const games =
            (_isGroups && torIdRequestGroups) || (!_isGroups && torIdRequest)
              ? await publicGetRequest({
                  id: _isGroups ? torIdRequestGroups : torIdRequest,
                  endpoint: _isGroups ? 'TOR_DRAWS_GROUPS' : 'TOR_DRAWS'
                })
              : []
          if (!_isGroups) {
            const maxPhase = games?.[0]?.NumberCouple / 2
            let _phases = []
            let idx = 0
            for (let round = maxPhase; round >= 1; round /= 2) {
              _phases.push(games.slice(idx, idx + round))
              idx = idx + round
            }
            _phases = _phases.filter((phase) => phase.length)
            if (games.find(({ Letter }) => Letter)) {
              const letterRound = AB.slice(
                0,
                _phases[_phases.length - 1].length
              ).map((letter) => ({
                letterRound: letter
              }))
              _phases.push(letterRound)
            }
            setPhases(_phases)
            const minDateByPhase = {}
            const maxDateByPhase = {}
            _phases.forEach((phase, idx) =>
              phase.forEach((match) => {
                if (
                  !minDateByPhase[idx] ||
                  (match.Date &&
                    new Date(minDateByPhase[idx]) > new Date(match.Date))
                ) {
                  minDateByPhase[idx] = match.Date
                }
                if (
                  !maxDateByPhase[idx] ||
                  new Date(maxDateByPhase[idx]) < new Date(match.Date)
                ) {
                  maxDateByPhase[idx] = match.Date
                }
              })
            )
            let _startingFromPhase
            const today = new Date()
            Object.keys(minDateByPhase).forEach((_, idx) => {
              const minDay = new Date(minDateByPhase[idx])
              const maxDay = new Date(maxDateByPhase[idx])
              if (
                !Number.isInteger(_startingFromPhase) &&
                (datesAreOnSameDay(today, minDay) ||
                  datesAreOnSameDay(today, maxDay) ||
                  (today < minDay && today < maxDay))
              ) {
                _startingFromPhase = idx
              }
            })
            let _phasesOpen = []
            if (window.innerWidth > 1140) {
              if (
                Number.isInteger(_startingFromPhase) &&
                _startingFromPhase + 2 <= _phases.length - 1
              ) {
                _phasesOpen = [
                  _startingFromPhase,
                  _startingFromPhase + 1,
                  _startingFromPhase + 2
                ]
              } else {
                _phasesOpen = [
                  _phases.length - 3,
                  _phases.length - 2,
                  _phases.length - 1
                ]
              }
            } else {
              _phasesOpen = [_startingFromPhase]
            }
            setPhasesOpen(_phasesOpen)
            setStartingFromPhase(_startingFromPhase)
            setMemoPhasesOpen(_phasesOpen)
          } else {
            const matchesClean = games.map((item) => ({
              date: item.DateTime
                ? dateToString(item.DateTime).full
                : 'No disponible',
              result: item?.Result?.replaceAll('|', '/'),
              teams: [
                {
                  name: capitalizeString(item.Couple1aName),
                  imageUrl: item.ImageLicense1a
                },
                {
                  name: capitalizeString(item.Couple1bName),
                  imageUrl: item.ImageLicense1b
                },
                {
                  name: capitalizeString(item.Couple2aName),
                  imageUrl: item.ImageLicense2a
                },
                {
                  name: capitalizeString(item.Couple2bName),
                  imageUrl: item.ImageLicense2b
                }
              ],
              played: item.Played === 'yes',
              playPlace: item.PlayPlace
            }))
            setPhases(matchesClean)
            const groupRes = await publicGetRequest({
              id: categoryFilterSelected,
              endpoint: 'TOR_DRAWS_GROUPS_TABLE'
            })
            setGroupTable(groupRes)
          }
          if (!torColor) {
            setTorColor(
              torsCats.find(({ newsCat }) => games[0]?.Category === newsCat)
                ?.color
            )
          }
          setDrawsMsgs(drawsMsgsList[categoryFilterSelected])
        } catch (err) {
          console.log(err)
        }
      } else {
        setPhases([])
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    getCategories()
  }, [genderFilterSelected])

  useEffect(() => {
    setGenderFilters()
  }, [categories])

  useEffect(() => {
    getDrawsData()
  }, [categoryFilterSelected, teamFilterSelected])

  const accordionLogic = (position) => {
    switch (position) {
      case 0:
        setPhasesOpen([position, position + 1, position + 2])
        break
      case phases.length - 1:
        setPhasesOpen([position - 2, position - 1, position])
        break
      default:
        setPhasesOpen([position - 1, position, position + 1])
        break
    }
  }

  const [isSmall, setIsSmall] = useState()
  useEffect(() => {
    const handleResize = () => {
      setIsSmall(window.innerWidth < 1140)
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isSmall) {
      let _phasesOpen
      if (
        Number.isInteger(startingFromPhase) &&
        startingFromPhase + 2 <= phases.length - 1
      ) {
        _phasesOpen = [
          startingFromPhase,
          startingFromPhase + 1,
          startingFromPhase + 2
        ]
        setStartingFromPhase(startingFromPhase)
      } else {
        _phasesOpen = [phases.length - 3, phases.length - 2, phases.length - 1]
      }
      setPhasesOpen(_phasesOpen)
    } else {
      if (startingFromPhase >= 0 || phases.length - 1 >= 0) {
        setPhasesOpen(
          startingFromPhase >= 0 ? [startingFromPhase] : [phases.length - 1]
        )
      }
    }
  }, [isSmall, startingFromPhase, phases])

  const getResults = (team, results, obs1, obs2, firstNameA, firstNameB) => {
    const matches = results.split(' / ')
    let result
    if (obs1 === 'WO') {
      result = team === 0 ? ['WO'] : ['-']
    } else if (obs2 === 'WO') {
      result = team === 1 ? ['WO'] : ['-']
    } else if (
      firstNameA.toLowerCase().includes('bye') ||
      firstNameB.toLowerCase().includes('bye')
    ) {
      result = ['-']
    }
    return result ? result : matches.map((match) => match.split('-')[team])
  }

  useEffect(() => {
    if (isPrinting && promiseResolveRef.current) {
      promiseResolveRef.current()
    }
  }, [isPrinting])

  const handlePrint = useReactToPrint({
    content: () => componentToPrint.current,
    onBeforeGetContent: () => {
      return new Promise((resolve) => {
        promiseResolveRef.current = resolve
        setPhasesOpen(
          Array.from(
            {
              length: phases.length
            },
            (_, i) => i
          )
        )
        setIsPrinting(true)
      })
    },
    onAfterPrint: () => {
      promiseResolveRef.current = null
      setPhasesOpen(memoPhasesOpen)
      setIsPrinting(false)
    }
  })

  return (
    <div
      className={`max-w-6xl overflow-hidden games ${
        !isTor ? 'mx-auto' : 'mx-3 lg:mx-10'
      }`}
    >
      {matchDaysIdSelected ? (
        <div>
          <GoBackAction
            action={() => {
              setMatchDaysIdSelected()
              setMatchData()
            }}
          />
          {/* <ResultsLeagues matches={matchData} torDetails={torDetails} /> */}
          {!matchData ? (
            <div className="flex justify-center my-20">
              <Spin color="text-blue" size="10" className="my-2 lg:my-8" />
            </div>
          ) : !matchData.length ? (
            <NoResults text="No hi ha informació" />
          ) : null}

          {matchData?.length ? (
            <div className="mt-8 lg:mt-16">
              <TeamsResultHeader teamsResult={teamsResult} />
            </div>
          ) : null}
          <div className="mt-8">
            <Timetable matches={matchData} />
          </div>
        </div>
      ) : (
        <div
          className={`mx-auto ${
            !groupTable?.length
              ? isPrinting
                ? 'w-[1704px]'
                : phases[0]?.length === 64
                ? 'w-[1250px]'
                : 'w-[1140px]'
              : ''
          }`}
        >
          {!isTor ? (
            <div className="lg:flex">
              <div className="grid grid-cols-1 lg:flex">
                <RadioFilter
                  filters={genderFilter}
                  radioSelected={genderFilterSelected}
                  setRadioSelected={setGenderFilterSelected}
                />
                <SelectFilter
                  filters={categoryFilter}
                  setSelected={setCategoryFilterSelected}
                  className="lg:w-[350px] lg:ml-3 mt-2 lg:mt-0"
                />
              </div>
              <button
                onClick={() => {
                  handlePrint()
                }}
                className="items-center ml-auto rounded-sm hidden"
              >
                <img src="/assets/icons/print.svg" className="w-[35px]" />
              </button>
            </div>
          ) : (
            <div className="mt-12">
              <Title>{teamFilterSelected?.Draws?.[0]?.nameDraw}</Title>
            </div>
          )}
          {(drawsMsgs?.main || teamFilterSelected?.Draws?.[0]?.mainComment) && (
            <div className="mt-8 font-alliance-medium text-[16px] lg:text-[22px] text-blue w-screen lg:w-full pr-20">
              {drawsMsgs?.main || teamFilterSelected?.Draws?.[0]?.mainComment}
            </div>
          )}
          {(drawsMsgs?.secondary ||
            teamFilterSelected?.Draws?.[0]?.secondaryComment) && (
            <div className="mt-8 font-alliance-medium text-[16px] lg:text-[22px] text-grey w-screen lg:w-full pr-20">
              {drawsMsgs?.secondary ||
                teamFilterSelected?.Draws?.[0]?.secondaryComment}
            </div>
          )}
          {showSpainTimezone ? (
            <div className="mt-8 font-alliance-medium text-[16px] lg:text-[22px] text-grey w-screen lg:w-full pr-20">
              {showSpainTimezone}
            </div>
          ) : null}
          {pdfLink ? (
            <div className="my-20">
              <Button link={pdfLink} text="DESCARREGA QUADRE" />
            </div>
          ) : phases.length ? (
            !isGroups ? (
              <div
                className="games-matches flex justify-center mt-16"
                ref={componentToPrint}
              >
                <div
                  className={`flex ${
                    isPrinting
                      ? 'w-[1704px]'
                      : phases[0]?.length === 64
                      ? 'w-[1250px]'
                      : 'w-[1140px]'
                  }`}
                >
                  {phasesOpen &&
                    phases.map((phase, idx) => (
                      <div
                        key={idx}
                        className={`games-column ${
                          phasesOpen.includes(idx) ? 'is-open' : ''
                        } ${
                          10 + (phases.length - 1 - idx) * 15 === 100
                            ? `bg-${torColor}`
                            : `bg-${torColor}/[.${
                                10 + (phases.length - 1 - idx) * 15
                              }]`
                        } hover:bg-${torColor} ${
                          isSmall && !phasesOpen.includes(idx)
                            ? 'force-hidden'
                            : ''
                        }`}
                        onClick={() => {
                          if (!isSmall) {
                            accordionLogic(idx)
                          }
                        }}
                      >
                        <h2
                          className={`w-full flex justify-between text-[22px] text-center font-alliance-medium text-blue uppercase absolute top-0 ${
                            !phasesOpen.includes(idx)
                              ? 'rotate-text mt-6 ml-2'
                              : 'm-6'
                          }`}
                        >
                          <img
                            className={`games-column-arrows ml-6 w-10 rotate-180 ${
                              !isSmall || !phasesOpen.includes(idx)
                                ? 'hidden'
                                : ''
                            }`}
                            src="/assets/icons/arrow-circle-bicolor.svg"
                            alt=""
                            onClick={() => {
                              if (phasesOpen[0]) {
                                setPhasesOpen((phasesOpen) => [
                                  phasesOpen[0] - 1
                                ])
                              }
                            }}
                          />
                          <span>{phase[0]?.RoundName}</span>
                          <img
                            className={`games-column-arrows mr-6 w-10 ${
                              !isSmall || !phasesOpen.includes(idx)
                                ? 'hidden'
                                : ''
                            }`}
                            src="/assets/icons/arrow-circle-bicolor.svg"
                            alt=""
                            onClick={() => {
                              if (phasesOpen[0] + 1 < phases.length) {
                                setPhasesOpen((phasesOpen) => [
                                  phasesOpen[0] + 1
                                ])
                              }
                            }}
                          />
                        </h2>
                        {phase.length &&
                          phase.map((match, idxMatch) => {
                            let matchDate = match.Played === 'no'
                            if (matchDate) {
                              matchDate = dateToString(match.DateTime)
                            }
                            const areTeams = match?.Team1
                            return (
                              <div
                                key={idxMatch}
                                className={`vs ${
                                  match?.letterRound
                                    ? 'vs--letter'
                                    : idx + 1 === phases.length - 1 &&
                                      phases[idx + 1][0]?.letterRound
                                    ? 'vs--nextletter'
                                    : ''
                                }`}
                              >
                                <div className="vs-body">
                                  {match?.letterRound ? (
                                    <p className="flex h-full justify-center items-center text-xs font-medium text-blue font-alliance-semibold mb-5">
                                      {`Lletra ${match.letterRound}`}
                                    </p>
                                  ) : (
                                    <>
                                      <div className="flex relative items-center h-12 border-1 border-b border-blue">
                                        {match.SeedNumber1 && idx === 0 ? (
                                          <div
                                            className={`bg-${torColor} absolute h-full left-0 flex items-center`}
                                          >
                                            <p className="text-xs font-bold text-blue font-alliance px-2">
                                              {match.SeedNumber1}
                                            </p>
                                          </div>
                                        ) : null}
                                        {/* <img
                            className="h-6 w-6 rounded-full object-cover object-top shadow-md mx-2"
                            src={
                              match.ImageLicense1a ||
                              '/assets/images/players/no-photo.svg'
                            }
                            alt=""
                          /> */}
                                        <p
                                          className={`text-xs font-medium text-blue ml-10 mr-4 font-alliance ${
                                            !areTeams ? 'w-[60px]' : ''
                                          } ${
                                            match.WinnerCouple1 ||
                                            match.Played === 'no'
                                              ? 'font-alliance-semibold'
                                              : 'opacity-40 font-alliance-medium'
                                          }`}
                                        >
                                          {areTeams
                                            ? capitalizeString(match.Team1)
                                            : formatName(match.Couple1aName)}
                                        </p>
                                        {/* <img
                            className="h-6 w-6 rounded-full object-cover object-top shadow-md mr-2"
                            src={
                              match.ImageLicense1b ||
                              '/assets/images/players/no-photo.svg'
                            }
                            alt=""
                          /> */}
                                        {!areTeams && (
                                          <p
                                            className={`text-xs font-medium text-blue ml-4 mr-4 w-[60px] font-alliance ${
                                              match.WinnerCouple1 ||
                                              match.Played === 'no'
                                                ? 'font-alliance-semibold'
                                                : 'opacity-40 font-alliance-medium'
                                            }`}
                                          >
                                            {formatName(match.Couple1bName)}
                                          </p>
                                        )}
                                        <div
                                          className={`relative h-full flex items-center justify-center min-w-16 ml-auto bg-${torColor}`}
                                        >
                                          <p className="text-xs font-bold text-blue">
                                            {!matchDate
                                              ? getResults(
                                                  0,
                                                  match.Result,
                                                  match.Obs1,
                                                  match.Obs2,
                                                  match.Couple1aName,
                                                  match.Couple2aName
                                                ).map((result, idx) => (
                                                  <span
                                                    key={idx}
                                                    className={`mr-2 ${
                                                      !match.WinnerCouple1 &&
                                                      match.Played === 'yes'
                                                        ? 'opacity-40'
                                                        : ''
                                                    }`}
                                                  >
                                                    {result}
                                                  </span>
                                                ))
                                              : matchDate.withYearMinimal}
                                          </p>
                                          {(match.ScheduleObs ||
                                            match.PlayPlace) && (
                                            <div
                                              className="absolute -bottom-4 z-10"
                                              key={idxMatch}
                                            >
                                              <a
                                                data-tooltip-id={`tooltip-${match.IdSchedule}`}
                                              >
                                                <ExclamationCircleIcon
                                                  className="h-8 w-8 text-blue"
                                                  aria-hidden="true"
                                                />
                                              </a>
                                              <Tooltip
                                                id={`tooltip-${match.IdSchedule}`}
                                              >
                                                <div>
                                                  {match.ScheduleObs && (
                                                    <p className="flex">
                                                      <InformationCircleIcon className="h-5 w-5 mr-2 shrink-0" />
                                                      {match.ScheduleObs}
                                                    </p>
                                                  )}
                                                  {match.PlayPlace && (
                                                    <p className="flex mt-2">
                                                      <MapPinIcon className="flex h-5 w-5 mr-2 shrink-0" />
                                                      {match.PlayPlace}
                                                    </p>
                                                  )}
                                                </div>
                                              </Tooltip>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {areTeams && match.IdResult ? (
                                        <div className="relative">
                                          <button
                                            className="absolute right-[21px] z-10 -top-[14px]"
                                            onClick={() => {
                                              setTeamsResult((data) => ({
                                                ...data,
                                                team1: match.Team1,
                                                team2: match.Team2
                                              }))
                                              setMatchDaysIdSelected(
                                                match.IdResult
                                              )
                                            }}
                                          >
                                            <div className="rounded-full bg-white p-[3px] border-2 border-white hover:border-blue">
                                              <img
                                                src={`/assets/icons/action-lineups.svg`}
                                                alt="icon"
                                                className="w-5 h-5"
                                              />
                                            </div>
                                          </button>
                                        </div>
                                      ) : null}
                                      <div className="flex relative items-center h-12 border-1 border-t border-blue">
                                        {match.SeedNumber2 && idx === 0 ? (
                                          <div
                                            className={`bg-${torColor} absolute h-full left-0 flex items-center`}
                                          >
                                            <p className="text-xs font-bold text-blue font-alliance px-2">
                                              {match.SeedNumber2}
                                            </p>
                                          </div>
                                        ) : null}
                                        {/* <img
                            className="h-6 w-6 rounded-full object-cover object-top shadow-md mx-2"
                            src={
                              match.ImageLicense2a ||
                              '/assets/images/players/no-photo.svg'
                            }
                            alt=""
                          /> */}
                                        <p
                                          className={`text-xs font-medium text-blue ml-10 mr-4 ${
                                            !areTeams ? 'w-[60px]' : ''
                                          } font-alliance ${
                                            match.WinnerCouple2 ||
                                            match.Played === 'no'
                                              ? 'font-alliance-semibold'
                                              : 'opacity-40 font-alliance-medium'
                                          }`}
                                        >
                                          {areTeams
                                            ? capitalizeString(match.Team2)
                                            : formatName(match.Couple2aName)}
                                        </p>
                                        {/* <img
                            className="h-6 w-6 rounded-full object-cover object-top shadow-md mr-2"
                            src={
                              match.ImageLicense2b ||
                              '/assets/images/players/no-photo.svg'
                            }
                            alt=""
                          /> */}
                                        {!areTeams && (
                                          <p
                                            className={`text-xs font-medium text-blue ml-4 mr-4 w-[60px] font-alliance ${
                                              match.WinnerCouple2 ||
                                              match.Played === 'no'
                                                ? 'font-alliance-semibold'
                                                : 'opacity-40 font-alliance-medium'
                                            }`}
                                          >
                                            {formatName(match.Couple2bName)}
                                          </p>
                                        )}
                                        <div
                                          className={`h-full flex items-center justify-center min-w-16 ml-auto bg-${torColor}`}
                                        >
                                          <p className="text-xs font-bold text-blue">
                                            {!matchDate
                                              ? getResults(
                                                  1,
                                                  match.Result,
                                                  match.Obs1,
                                                  match.Obs2,
                                                  match.Couple1aName,
                                                  match.Couple2aName
                                                ).map((result, idx) => (
                                                  <span
                                                    key={idx}
                                                    className={`mr-2 ${
                                                      !match.WinnerCouple2 &&
                                                      match.Played === 'yes'
                                                        ? 'opacity-40'
                                                        : ''
                                                    }`}
                                                  >
                                                    {result}
                                                  </span>
                                                ))
                                              : matchDate.time}
                                          </p>
                                        </div>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )
                          })}
                      </div>
                    ))}
                </div>
              </div>
            ) : (
              <>
                <Timetable matches={phases} />
                {groupTable.length ? (
                  <TableStaticPagination
                    headers={[
                      '',
                      'Parella',
                      '',
                      '',
                      '',
                      'Punts',
                      'J',
                      'G',
                      'P',
                      'Set Favor / Contra',
                      'Jocs Favor / Contra'
                    ]}
                    content={groupTable.map((row) => ({
                      0: row.Classification,
                      1: `${formatName(row.Couple1Name)} / ${formatName(
                        row.Couple2Name
                      )}`,
                      4: row.Points,
                      5: row.MatchsPlayed,
                      6: row.MatchsWon,
                      7: row.MatchsLosed,
                      8: `${row.SetWon} / ${row.SetLoses}`,
                      9: `${row.GameWon} / ${row.GameLosed}`
                    }))}
                    cols={11}
                    extraClass={{ 1: 'col-span-4 pr-8' }}
                    headersMobile={[
                      '',
                      'Parella',
                      '',
                      '',
                      'P',
                      'J',
                      'G',
                      'DS',
                      'DJ'
                    ]}
                    contentMobile={groupTable.map((row) => ({
                      0: row.Classification,
                      1: `${formatName(row.Couple1Name)} / ${formatName(
                        row.Couple2Name
                      )}`,
                      4: row.Points,
                      5: row.MatchsPlayed,
                      6: row.MatchsWon,
                      7: `${row.SetWon}/${row.SetLoses}`,
                      8: `${row.GameWon}/${row.GameLosed}`
                    }))}
                    extraClassMobile={{ 1: 'col-span-3 pr-4' }}
                    forceRowsInMobile={true}
                    colsMobile={9}
                  />
                ) : null}
              </>
            )
          ) : loading ? (
            <div className="flex justify-center my-20">
              <Spin color="text-blue" size="10" className="my-2 lg:my-8" />
            </div>
          ) : (
            phases && <NoResults />
          )}
        </div>
      )}
    </div>
  )
}

export default Games
